import React from 'react'
import {
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDataSource,
  type CrossClientQueryLogicalQuestionDecomposition,
} from '../../../models/CrossClient'
import { CrossClientResultSourceIcon } from '../Results/Sources/CrossClientResultSourceIcon'
import { ReactComponent as ZocksIcon } from '../../../assets/images/ZocksIcon-primary.svg'
import { ReactComponent as StepRecordsIcon } from '../../../assets/images/cross-client-steps-records.svg'
import { ReactComponent as StepAiIcon } from '../../../assets/images/cross-client-steps-ai.svg'
import { CLIENT_MODE_LABEL, getIntegrationSourceDisplayNames } from '../utils'

interface HowItWillRunRowProps {
  icon: React.ReactNode
  isLast?: boolean
  children: React.ReactNode
}

const HowItWillRunRow: React.FC<HowItWillRunRowProps> = ({ icon, isLast = false, children }) => (
  <div className='d-flex gap-2 align-items-start'>
    <div className='d-flex justify-content-center scheduled-query-run-rail'>{icon}</div>
    <div className={isLast ? '' : 'pb-4'}>{children}</div>
  </div>
)

export interface ScheduledQueryDetails {
  userQuestion: string
  clientQueryMode?: CrossClientQueryClientQueryMode | null
  sources: CrossClientQueryDataSource[]
  decomposition?: CrossClientQueryLogicalQuestionDecomposition | null
  resolvableClauseVariables?: string[] | null
  localResolvableClauseVariables?: string[] | null
}

interface ScheduledQueryDetailsCardProps {
  details: ScheduledQueryDetails
}

export const ScheduledQueryDetailsCard: React.FC<ScheduledQueryDetailsCardProps> = ({ details }) => {
  const { userQuestion, clientQueryMode, sources, decomposition } = details
  const hasBulk = details.resolvableClauseVariables == null || details.resolvableClauseVariables.length > 0
  const hasLocal = details.localResolvableClauseVariables == null || details.localResolvableClauseVariables.length > 0
  const hasZocksSource = sources.some((source) => source.type === 'clientProfile' || source.type === 'meeting')
  const integrationTypes = [...new Set(sources.filter((source) => source.type === 'integration').map((source) => source.integrationType))]
  const integrationNames = getIntegrationSourceDisplayNames(sources)

  const rows: { label: string; content: React.ReactNode }[] = [{ label: 'Question', content: <span className='paragraph-small'>{userQuestion}</span> }]
  if (clientQueryMode) {
    rows.push({ label: 'Clients', content: <span className='paragraph-small'>{CLIENT_MODE_LABEL[clientQueryMode]}</span> })
  }
  if (sources.length > 0) {
    rows.push({
      label: 'Sources',
      content: (
        <div className='d-flex gap-1 align-items-center'>
          {hasZocksSource ? <ZocksIcon width='1rem' height='1rem' aria-hidden='true' focusable='false' /> : null}
          {integrationTypes.map((integrationType) => (
            <CrossClientResultSourceIcon key={integrationType} source={{ type: 'integration', integrationType }} size='md' />
          ))}
        </div>
      ),
    })
  }
  const atomicClauses = decomposition?.atomicClauses ?? []
  if (atomicClauses.length > 0) {
    rows.push({
      label: 'Criteria',
      content: (
        <div className='d-flex flex-column gap-2'>
          {atomicClauses.map((clause) => (
            <span key={clause.variable} className='paragraph-small'>
              {clause.description}
            </span>
          ))}
        </div>
      ),
    })
  }

  return (
    <div className='d-flex flex-column gap-4 px-3 py-4 border border-1 zocks-border-color-gray-200 zocks-bg-color-white rounded-4'>
      <div className='d-flex flex-column gap-4'>
        <h6 className='mb-0'>Query details</h6>
        <div className='d-flex flex-column gap-3'>
          {rows.map(({ label, content }) => (
            <div key={label} className='d-flex gap-2 align-items-start'>
              <span className='paragraph-small medium zocks-text-gray-600 scheduled-query-details-label'>{label}</span>
              {content}
            </div>
          ))}
        </div>
      </div>
      <hr className='my-0' />
      <div className='d-flex flex-column gap-3'>
        <h6 className='mb-0'>How it will run</h6>
        <div className='d-flex flex-column position-relative scheduled-query-run-list'>
          {hasBulk ? (
            <HowItWillRunRow icon={<i className='bi bi-lightning-charge scheduled-query-run-icon' aria-hidden='true' />}>
              <span className='paragraph-small'>Find matches in Client Profiles in Zocks</span>
            </HowItWillRunRow>
          ) : null}
          {hasLocal && integrationNames.length > 0 ? (
            <HowItWillRunRow icon={<StepRecordsIcon width={18} height={18} aria-hidden='true' />}>
              <span className='paragraph-small'>Go beyond Zocks with richer records from {integrationNames.join(' and ')}</span>
            </HowItWillRunRow>
          ) : null}
          <HowItWillRunRow icon={<StepAiIcon width={18} height={18} aria-hidden='true' />} isLast>
            <div className='d-flex flex-column gap-1'>
              <span className='paragraph-small'>Finds matches hidden in scattered data</span>
              <span className='paragraph-small zocks-text-gray-700'>
                I&apos;ll gather everything across meetings, notes and integrations for each client and analyze it together.
              </span>
            </div>
          </HowItWillRunRow>
        </div>
      </div>
    </div>
  )
}
